import { Link } from "react-router-dom";
import type { MediaItem } from "@/types/media";
import { typeLabel } from "@/utils/media";

interface MediaRowProps {
  item: MediaItem;
  index?: number;
}

export default function MediaRow({ item, index = 0 }: MediaRowProps) {
  return (
    <Link
      to={`/detail/${item.id}`}
      className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-all duration-300 hover:border-primary-300 hover:bg-primary-50 md:gap-5 md:p-4"
      style={{ animation: `fadeInUp 0.45s ease-out ${index * 0.04}s both` }}
    >
      <div className="relative h-40 w-28 shrink-0 overflow-hidden rounded-md md:h-48 md:w-32">
        <img
          src={item.cover}
          alt={`${item.title}封面`}
          title={`${item.title} - 影视详情`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute right-1.5 top-1.5 flex items-center gap-0.5 rounded-md bg-accent-500 px-1.5 py-0.5 text-[11px] font-bold text-accent-950">
          <i className="ri-star-fill text-[9px]" aria-hidden="true" />
          {item.rating.toFixed(1)}
        </span>
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
            {item.title}
          </h3>
          <span className="hidden shrink-0 rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900 sm:inline-block">
            {typeLabel[item.type]}
          </span>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line" aria-hidden="true" />
            {item.year}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-map-pin-line" aria-hidden="true" />
            {item.region}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line" aria-hidden="true" />
            {item.duration}
          </span>
        </div>

        <div className="mt-2 flex flex-wrap gap-1.5">
          {item.genres.map((genre) => (
            <span
              key={genre}
              className="rounded-md bg-background-100 px-2 py-0.5 text-[11px] font-medium text-foreground-700"
            >
              {genre}
            </span>
          ))}
        </div>

        <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-foreground-600">
          {item.synopsis}
        </p>

        <div className="mt-auto flex items-center justify-between gap-3 pt-3">
          <span className="hidden items-center gap-2 text-xs text-foreground-400 sm:flex">
            <i className="ri-user-star-line" aria-hidden="true" />
            导演：{item.director}
          </span>
          <span className="flex items-center gap-1 text-xs font-medium text-primary-700">
            查看详情
            <i
              className="ri-arrow-right-s-line transition-transform group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </span>
        </div>
      </div>
    </Link>
  );
}